


import { Component, Vue } from "vue-property-decorator";

import { Roles, Records } from "App";
import { Role, CustomError, PagerSorter, Column } from "App/Models";
import { Filter } from "App/Contracts";
import { EventBus, $t } from "App/IOC";

import { TableAction } from "Web/Contracts";

import RecordsComponent from "Web/Components/Records.vue";
import RoleForm from "./Form.vue";

@Component({
    components: {
        RoleForm,
        Records: RecordsComponent
    }
})
export default class RolesView extends Vue {
    $refs!: Vue[ "$refs" ] & {
        Records: RecordsComponent,
    };

    private columns: Column[] = [];
    private records: Role[] = [];

    private id: string = "Roles";

    private pagerSorter: PagerSorter = PagerSorter.ForColumn(
        Roles.Columns.GetDefaultSort(), 15, false
    );

    private actions: TableAction<Role>[] = [
        {
            Id: "delete",
            ShortName: "delete",
            Name: $t( "GLOBAL_actionDelete" ),
            Multi: false
        }
    ];

    private showForm: boolean = false;

    private async created() {
        const [ columns, error ] = await Roles.Columns.Get();

        if( error !== undefined ) {
            EventBus.Publish( "Error", error );
            return;
        }

        this.columns = columns;
    }

    /* The list table reads record[ column.Id ]; "Privileges" is a derived count, so it is stamped
       on each record after fetch. */
    private decorate( roles: Role[] ): void {
        for( const role of roles ) {
            const count = Roles.Privileges.GrantedCount( role );

            ( role as any ).Privileges = this.$tc( "ROLES_grantedCount", count );
        }
    }

    private async update(
        filters: Filter[],
        pagerSorter: PagerSorter,
        resetPagination?: boolean
    ) {
        await this.$nextTick();
        this.$refs.Records.Loading();

        if( resetPagination )
            this.pagerSorter.Page = 1;

        const [ records, updatedPagerSorter, error ] = await Roles.Find( filters, pagerSorter );

        /* The protected role (account owner / Super Admin) has every right by default and
           nothing to configure, so it is never listed. */
        const visible = records.filter( record => record.IsProtected !== true );

        updatedPagerSorter.Total = Math.max(
            0, updatedPagerSorter.Total - ( records.length - visible.length )
        );

        this.decorate( visible );

        this.records = visible;
        this.pagerSorter = updatedPagerSorter;

        if( error !== undefined )
            EventBus.Publish( "Error", error );
    }

    private toggleColumn( column: Column ) {
        Roles.Columns.Toggle( column );
    }

    private executeAction( id: string, roles: Role[] ) {
        const urls: string[] = [];
        let index: number = 0;

        switch( id ) {
            case "open":
                this.records.forEach( record => urls.push( "/settings/roles/" + record.Id ) );

                index = this.records.findIndex( record => record.Id === roles[ 0 ].Id );

                Records.Navigation.SetList( urls, index );

                this.$router.push( `/settings/roles/${roles[ 0 ].Id}` );

                break;

            case "delete":
                EventBus.Publish( "Confirmation", {
                    Props: {
                        Message: this.$tc( "ROLES_deleteConfirm", roles.length ),

                        Actions: [{
                            Id: "delete",
                            Name: this.$t( "GLOBAL_actionDelete" ),
                            Active: true
                        }]
                    },

                    Events: { Action: () => this.delete( roles ) }
                });

                break;

            default:
                EventBus.Publish( "UnderConstruction" );
                break;
        }
    }

    private async delete( records: Role[] ) {
        for( const record of records ) {
            const error = await Roles.Delete( record );

            if( error !== undefined )
                EventBus.Publish(
                    "Error",
                    new CustomError( "Error", $t( "ROLES_cannotDelete" ) )
                );
        }

        this.refresh();
    }

    private refresh(): void {
        this.showForm = false;
        this.update( this.$refs.Records.AppliedFilters, this.pagerSorter, true );
    }
}
